import { Injectable } from '@angular/core';
import {
  API_CONFIG,
  BaseCrudService,
  buildSearchByTerm,
  PageResponse,
} from '@nebula-shared/data-access';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PageRequest, TerceroResponse } from '../../domain/maestro-terceros-modal.models';
import type { CriterioOrden } from '../../../select-dialog/orden-dialogo';

@Injectable({ providedIn: 'root' })
export class TercerosService extends BaseCrudService<TerceroResponse> {
  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}/terceros`);
  }

  /**
   * Ruta del page-response de terceros, la que consulta la ventana de busqueda de maestros con el
   * filtro fijo de terceros activos; el servidor pagina, busca y ordena.
   *
   * @returns la ruta completa del page-response de terceros
   */
  get rutaDePageResponse(): string {
    return `${this.baseUrl}/page-response`;
  }
  getTercerosPaginados(
    page = 0,
    size = 10,
    searchTerm = '',
    soloActivos = true,
    tipoTercero?: string,
    terceroIdentificacion?: string,
    ordersBy?: CriterioOrden[],
  ): Observable<PageResponse<TerceroResponse>> {
    const searchsBy = [];

    // El estado de negocio del tercero es `estadoTercero` en la vista que pagina el servidor
    // (TerceroFullView). Con 'estado' el servidor descartaba el filtro en silencio y el selector
    // traia tambien los inactivos (R-E02, correccion de status de Terceros, tablero 2j).
    if (soloActivos) {
      searchsBy.push({
        key: 'estadoTercero',
        operation: 'EQUAL',
        value: 'ACTIVO',
        linkOperator: 'AND',
      });
    }

    if (searchTerm && searchTerm.trim() !== '') {
      searchsBy.push(
        {
          key: 'numeroDocumento',
          operation: 'MATCH',
          value: searchTerm,
          linkOperator: 'OR',
        },
        {
          key: 'nombreCompleto',
          operation: 'MATCH',
          value: searchTerm,
        },
      );
    }

    const body: PageRequest = {
      page: page.toString(),
      size: size.toString(),
      searchsBy: searchsBy,
      ordersBy: ordersBy?.length ? ordersBy : [{ key: 'numeroDocumento', direction: 'ASC' }],
    };

    return this.http.post<any>(`${this.baseUrl}/page-response`, body).pipe(
      map((response) => {
        const data = response?.response?.content || response;
        return {
          content: data?.content || [],
          totalElements: data?.totalElements || 0,
          totalPages: data?.totalPages || 0,
          size: data?.size || size,
          number: data?.number || page,
          numberOfElements: data?.content?.length || 0,
          first: page === 0,
          last: page >= (data?.totalPages || 1) - 1,
          empty: data?.content?.length === 0,
        } as PageResponse<TerceroResponse>;
      }),
    );
  }

  /**
   * Tercero activo con ese numero de documento exacto (sin DV), o null. Filtra en el servidor por
   * `numeroDocumento` y `estadoTercero` de la vista que pagina page-response (TerceroFullView).
   */
  buscarPorDocumento(numeroDocumento: string): Observable<TerceroResponse | null> {
    return this.primeroDe<TerceroResponse>('page-response', [
      this.criterioDocumento(numeroDocumento),
      { key: 'estadoTercero', operation: 'EQUAL', value: 'ACTIVO' },
    ]);
  }

  /** Tercero activo con vinculo PROVEEDOR y ese numero de documento exacto (sin DV), o null. */
  buscarProveedorPorDocumento(numeroDocumento: string): Observable<TerceroResponse | null> {
    return this.buscarPorVinculoYDocumento('PROVEEDOR', numeroDocumento);
  }

  /** Tercero activo con vinculo CLIENTE y ese numero de documento exacto (sin DV), o null. */
  buscarClientePorDocumento(numeroDocumento: string): Observable<TerceroResponse | null> {
    return this.buscarPorVinculoYDocumento('CLIENTE', numeroDocumento);
  }

  /**
   * La vista de terceros no tiene el tipo de vinculo: se busca el vinculo en page-response-vinculo
   * (TerceroVinculoView: tipoVinculo, numeroDocumento, estado) y el tercero se lee por get-record
   * con su terceroId.
   */
  private buscarPorVinculoYDocumento(
    tipoVinculo: string,
    numeroDocumento: string,
  ): Observable<TerceroResponse | null> {
    return this.primeroDe<{ terceroId: number }>('page-response-vinculo', [
      { key: 'tipoVinculo', operation: 'EQUAL', value: tipoVinculo },
      this.criterioDocumento(numeroDocumento),
      { key: 'estado', operation: 'EQUAL', value: 'ACTIVO' },
    ]).pipe(switchMap((vinculo) => (vinculo ? this.getRecordById(vinculo.terceroId) : of(null))));
  }

  /** Documento exacto, sin el DV que trae el control (`900123-4`). */
  private criterioDocumento(numeroDocumento: string) {
    return {
      key: 'numeroDocumento',
      operation: 'EQUAL',
      value: numeroDocumento.split('-')[0].trim(),
    };
  }

  /** Primer registro del page-response indicado con los criterios unidos por AND, o null. */
  private primeroDe<R>(
    ruta: string,
    criterios: { key: string; operation: string; value: string }[],
  ): Observable<R | null> {
    const body: PageRequest = {
      page: '0',
      size: '1',
      searchsBy: criterios.map((c, i) =>
        i < criterios.length - 1 ? { ...c, linkOperator: 'AND' } : c,
      ),
      ordersBy: [{ key: 'numeroDocumento', direction: 'ASC' }],
    };
    return this.http.post<any>(`${this.baseUrl}/${ruta}`, body).pipe(
      map((response) => {
        const data = response?.response?.content || response;
        return (data?.content?.[0] as R) ?? null;
      }),
    );
  }

  getAllTerceros(soloActivos = true, tipoTercero?: string): Observable<TerceroResponse[]> {
    return this.getTercerosPaginados(0, 100, '', soloActivos, tipoTercero).pipe(
      map((firstPage) => {
        const allContent = [...firstPage.content];
        return allContent;
      }),
    );
  }
  getTercerosConOpPaginados(
    page = 0,
    size = 10,
    searchTerm = '',
    sortColumn?: string,
    sortDirection: 'ASC' | 'DESC' = 'ASC',
  ): Observable<PageResponse<TerceroResponse>> {
    const searchsBy = buildSearchByTerm(searchTerm, [
      { key: 'terceroNombre', type: 'text' },
      { key: 'terceroIdentificación', type: 'text' },
    ]);

    const ordersBy: { column: string; direction: 'ASC' | 'DESC' }[] = [];
    if (sortColumn) {
      ordersBy.push({ column: sortColumn, direction: sortDirection });
    }

    const filters = {
      page,
      size,
      ...(searchsBy.length > 0 && { searchsBy }),
      ...(ordersBy.length > 0 && { ordersBy }),
    };

    return this.http.post<any>(`${this.baseUrl}/ordenes-pago/aprobadas-o-con-saldo`, filters).pipe(
      map((response) => {
        const data = response?.response?.content || response;
        return {
          content: data?.content || [],
          totalElements: data?.totalElements || 0,
          totalPages: data?.totalPages || 0,
          size: data?.size || size,
          number: data?.number || page,
          numberOfElements: data?.content?.length || 0,
          first: page === 0,
          last: page >= (data?.totalPages || 1) - 1,
          empty: data?.content?.length === 0,
        } as PageResponse<TerceroResponse>;
      }),
    );
  }
}
